import type { ReactNode } from 'react';
import { AlertTriangle, TrendingUp } from 'lucide-react';
import { useLimit } from '@/contexts/SubscriptionContext';
import { Card, CardContent, Button } from '@/components/ui';
import type { LimitName } from '@/types/subscription';
import type { SubscriptionTier } from '@/types';

const LIMIT_NAMES: Record<LimitName, string> = {
  maxUnits: 'units',
  maxUsers: 'team members',
  maxWorkOrdersPerMonth: 'work orders this month',
  maxDevices: 'IoT devices',
  maxProperties: 'properties',
  storageGB: 'storage',
};

const TIER_NAMES: Record<SubscriptionTier, string> = {
  free: 'Free',
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

interface LimitGateProps {
  limit: LimitName;
  currentValue?: number;
  children: ReactNode;
  /**
   * What to show when limit is reached
   * - 'warning': Show warning message with upgrade CTA (default)
   * - 'block': Block action entirely with upgrade prompt
   * - 'hidden': Hide completely
   * - ReactNode: Custom fallback
   */
  fallback?: 'warning' | 'block' | 'hidden' | ReactNode;
  /**
   * Custom message when limit reached
   */
  message?: string;
  onUpgrade?: () => void;
}

export function LimitGate({
  limit,
  currentValue,
  children,
  fallback = 'warning',
  message,
  onUpgrade,
}: LimitGateProps) {
  const { allowed, current, max, percentage, upgradeRequired } = useLimit(limit, currentValue);

  // Within limits - show children
  if (allowed) {
    return <>{children}</>;
  }

  const limitName = LIMIT_NAMES[limit];
  const displayMessage = message || `You've reached the limit of ${max} ${limitName}.`;

  // Hidden mode
  if (fallback === 'hidden') {
    return null;
  }

  // Custom fallback
  if (fallback !== 'warning' && fallback !== 'block') {
    return <>{fallback}</>;
  }

  // Block mode - show upgrade prompt
  if (fallback === 'block') {
    return (
      <Card className="border-warning-200 dark:border-warning-500/30 bg-warning-50 dark:bg-warning-500/10">
        <CardContent className="py-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-warning-100 dark:bg-warning-500/20">
            <AlertTriangle className="h-6 w-6 text-warning-600 dark:text-warning-400" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-secondary-900 dark:text-secondary-100">
            Limit Reached
          </h3>
          <p className="mb-4 text-sm text-secondary-600 dark:text-secondary-400">
            {displayMessage}
            {upgradeRequired && ` Upgrade to ${TIER_NAMES[upgradeRequired]} for more.`}
          </p>
          <div className="mb-4 flex justify-center gap-4 text-sm">
            <div className="text-center">
              <p className="font-semibold text-secondary-900 dark:text-secondary-100">{current}</p>
              <p className="text-secondary-500">Used</p>
            </div>
            <div className="text-center">
              <p className="font-semibold text-secondary-900 dark:text-secondary-100">{max}</p>
              <p className="text-secondary-500">Limit</p>
            </div>
          </div>
          {upgradeRequired && (
            <Button
              variant="primary"
              onClick={onUpgrade || (() => window.location.href = '/billing')}
            >
              <TrendingUp className="mr-2 h-4 w-4" />
              Upgrade Plan
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Warning mode - show children with warning banner
  return (
    <div>
      <div className="mb-4 rounded-lg bg-warning-50 dark:bg-warning-500/10 border border-warning-200 dark:border-warning-500/30 p-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-warning-600 dark:text-warning-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-warning-800 dark:text-warning-300">
              {displayMessage}
            </p>
            <div className="mt-2 flex items-center gap-4">
              <div className="flex-1 h-2 rounded-full bg-warning-200 dark:bg-warning-500/30">
                <div
                  className="h-2 rounded-full bg-warning-500"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="text-xs font-medium text-warning-700 dark:text-warning-400">
                {current}/{max}
              </span>
            </div>
            {upgradeRequired && (
              <button
                onClick={onUpgrade || (() => window.location.href = '/billing')}
                className="mt-2 text-sm font-medium text-warning-700 dark:text-warning-400 hover:underline"
              >
                Upgrade to {TIER_NAMES[upgradeRequired]} for more
              </button>
            )}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}

// Warning banner for approaching limits (>80%)
interface LimitWarningProps {
  limit: LimitName;
  currentValue?: number;
  showAt?: number; // Show at this percentage (default 80)
}

export function LimitWarning({ limit, currentValue, showAt = 80 }: LimitWarningProps) {
  const { allowed, current, max, percentage, upgradeRequired } = useLimit(limit, currentValue);

  // Don't show if within comfortable range or at limit (LimitGate handles that)
  if (!allowed || percentage < showAt || max === null) {
    return null;
  }

  const limitName = LIMIT_NAMES[limit];
  const remaining = max - current;

  return (
    <div className="rounded-lg bg-warning-50 dark:bg-warning-500/10 border border-warning-200 dark:border-warning-500/30 px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-warning-500" />
          <span className="text-warning-700 dark:text-warning-300">
            {remaining} {limitName} remaining
          </span>
        </div>
        {upgradeRequired && (
          <a
            href="/billing"
            className="font-medium text-warning-700 dark:text-warning-400 hover:underline"
          >
            Upgrade
          </a>
        )}
      </div>
    </div>
  );
}
